import React, { forwardRef, useCallback, useRef, useEffect } from "react";

import { Button } from "@/src/components/shadcn/ui/button";
import { type ButtonProps } from "@/src/components/shadcn/ui/button";
import { cn } from "@/src/lib/shadcn-utils";

interface CTAButtonProps extends ButtonProps {
  children: React.ReactNode;
  className?: string;
  small?: boolean;
}

const CTAButton = forwardRef<HTMLButtonElement, CTAButtonProps>(function CTAButton(
  { children, className, small, ...props },
  forwardedRef
) {
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Merge the forwarded ref (e.g. Radix's Slot when used via `asChild`) with our internal ref so
  // consumers can restore focus/keyboard-navigate to the underlying button while cmd+Enter still works.
  const setRefs = useCallback(
    (node: HTMLButtonElement | null) => {
      buttonRef.current = node;

      if (typeof forwardedRef === "function") {
        forwardedRef(node);
      } else if (forwardedRef) {
        forwardedRef.current = node;
      }
    },
    [forwardedRef]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey && event.key === "Enter") {
        event.preventDefault();

        buttonRef.current?.focus();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <Button
      ref={setRefs}
      className={cn(
        small
          ? "px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2 text-xs font-normal"
          : "w-full max-w-2xl p-6 focus:outline-none focus:ring-2 focus:ring-offset-2",
        className
      )}
      {...props}
    >
      {children}
    </Button>
  );
});

export default CTAButton;
